import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Snackbar,
    InputAdornment,
    Grid,
    Divider
} from '@mui/material';
import { Add, Edit, Search as SearchIcon, Category as CategoryIcon, Layers } from '@mui/icons-material';

import { useQueryClient } from '@tanstack/react-query';
import { useAdminProductCategories, useCreateAdminProductCategory, useUpdateAdminProductCategory } from 'hooks/useAdminCategories';

export default function CategoryList() {
    const queryClient = useQueryClient();

    // --- State ---
    const [searchTerm, setSearchTerm] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // --- Data Hooks ---
    const { data: categoriesRes, isLoading } = useAdminProductCategories();
    const categories = categoriesRes?.data || [];

    const filteredCategories = categories.filter((cat) => cat.name.toLowerCase().includes(searchTerm.toLowerCase()));

    // --- Mutation Hooks ---
    const createMutation = useCreateAdminProductCategory();

    const updateMutation = useUpdateAdminProductCategory();

    // --- Handlers ---
    const handleSuccess = (msg) => {
        setSnackbar({ open: true, message: msg, severity: 'success' });
        queryClient.invalidateQueries({ queryKey: ['adminProductCategories'] });
        handleCloseModal();
    };

    const handleError = (err) => {
        setSnackbar({ open: true, message: err?.response?.data?.message || 'Action failed', severity: 'error' });
    };

    const handleOpenModal = (category = null) => {
        setSelectedCategory(category);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedCategory(null);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        const payload = {
            name: formData.get('name'),
            description: formData.get('description')
        };

        try {
            if (selectedCategory) {
                await updateMutation.mutateAsync({ id: selectedCategory._id, payload });
                handleSuccess('Category updated!');
            } else {
                await createMutation.mutateAsync(payload);
                handleSuccess('Category created!');
            }
        } catch (err) {
            handleError(err);
        }
    };

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        Product Categories
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage general product groupings for the platform
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()} sx={{ borderRadius: 2 }}>
                    Add New Category
                </Button>
            </Stack>

            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2.5, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                TOTAL CATEGORIES
                            </Typography>
                            <Typography variant="h5" fontWeight={800}>
                                {categories?.length || 0}
                            </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'primary.light', p: 1 }}>
                            <Layers color="primary" />
                        </Avatar>
                    </Paper>
                </Grid>
            </Grid>

            {/* Table */}
            <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <Box p={3}>
                    <TextField
                        placeholder="Search categories..."
                        size="small"
                        fullWidth
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ maxWidth: 400 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, pl: 3 }}>NAME</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>DESCRIPTION</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, pr: 3 }}>
                                    ACTIONS
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" py={5}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCategories.map((cat) => (
                                    <TableRow key={cat._id} hover>
                                        <TableCell sx={{ pl: 3 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CategoryIcon fontSize="small" color="action" />
                                                <Typography variant="subtitle2" fontWeight={700}>
                                                    {cat.name}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell color="text.secondary">{cat.description || 'No description'}</TableCell>
                                        <TableCell align="right" sx={{ pr: 3 }}>
                                            <IconButton size="small" onClick={() => handleOpenModal(cat)} sx={{ border: '1px solid #eee' }}>
                                                <Edit fontSize="small" color="primary" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Modal */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                fullWidth
                maxWidth="xs"
                PaperProps={{ component: 'form', onSubmit: handleSubmit, sx: { borderRadius: 3 } }}
            >
                <DialogTitle fontWeight={700}>{selectedCategory ? 'Edit' : 'Create'} Category</DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack spacing={3} mt={1}>
                        <TextField label="Category Name" name="name" fullWidth required defaultValue={selectedCategory?.name || ''} />
                        <TextField
                            label="Description"
                            name="description"
                            fullWidth
                            multiline
                            rows={2}
                            defaultValue={selectedCategory?.description || ''}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseModal} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={createMutation.isPending || updateMutation.isPending}
                        startIcon={(createMutation.isPending || updateMutation.isPending) && <CircularProgress size={16} color="inherit" />}
                    >
                        {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Category'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

function Avatar({ children, sx }) {
    return (
        <Box
            sx={{
                width: 45,
                height: 45,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...sx
            }}
        >
            {children}
        </Box>
    );
}
